import { useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  Legend,
} from "recharts";
import type { ProjectionRow } from "@/lib/retirement-calc";

interface Props {
  rows: ProjectionRow[];
  currency: string;
  locale: string;
  shariahMode: boolean;
  targetAge: number;
}

const ProjectionChart = ({ rows, currency, locale, shariahMode, targetAge }: Props) => {
  const fmtFull = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }),
    [locale, currency]
  );
  const fmtCompact = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 1,
        notation: "compact",
      }),
    [locale, currency]
  );

  // Chart shows today's money (real), with the constant target line.
  const data = rows.map((r) => ({
    age: r.age,
    balance: Math.round(r.real),
    target: Math.round(r.targetToday),
    nominal: Math.round(r.nominal),
  }));

  const tickInterval = rows.length > 30 ? 4 : rows.length > 15 ? 2 : 0;

  return (
    <div className="w-full h-64 sm:h-80">
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 16, right: 12, left: 0, bottom: 8 }}>
          <defs>
            <linearGradient id="gBalance" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.55} />
              <stop offset="60%" stopColor="hsl(var(--primary))" stopOpacity={0.15} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 6" stroke="hsl(var(--border))" vertical={false} />
          <XAxis
            dataKey="age"
            stroke="hsl(var(--muted-foreground))"
            interval={tickInterval || "preserveStartEnd"}
            tickFormatter={(v) => `${v}`}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fontSize: 12 }}
          />
          <YAxis
            stroke="hsl(var(--muted-foreground))"
            tickFormatter={(v) => fmtCompact.format(v)}
            width={56}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            cursor={{ stroke: "hsl(var(--primary))", strokeWidth: 1, strokeDasharray: "4 4" }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const balance = payload.find((p) => p.dataKey === "balance")?.value as number;
              const target = payload.find((p) => p.dataKey === "target")?.value as number;
              const nominal = payload[0]?.payload?.nominal as number;
              const gap = balance - target;
              return (
                <div className="rounded-xl border border-border bg-background/95 backdrop-blur px-4 py-3 shadow-xl">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    Age {label}
                  </div>
                  <div className="flex items-center gap-2 text-sm mb-1">
                    <span className="w-2.5 h-2.5 rounded-sm bg-primary" />
                    <span className="text-foreground/70">Balance (today's $)</span>
                    <span className="ms-auto font-bold tabular-nums">{fmtFull.format(balance)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-2.5 h-2.5 rounded-sm border border-dashed border-muted-foreground" />
                    <span className="text-foreground/70">Target (25× expenses)</span>
                    <span className="ms-auto font-bold tabular-nums">{fmtFull.format(target)}</span>
                  </div>
                  <div
                    className="mt-1.5 text-xs tabular-nums font-semibold"
                    style={{ color: gap >= 0 ? "hsl(var(--primary))" : "hsl(var(--destructive))" }}
                  >
                    {gap >= 0 ? "+" : ""}
                    {fmtFull.format(gap)} vs target
                  </div>
                  <div className="mt-1.5 pt-1.5 border-t border-border text-[10px] text-muted-foreground leading-snug">
                    <div>Future $: {fmtFull.format(nominal)}</div>
                    {shariahMode && <div>Includes ~2.58%/yr Zakat.</div>}
                  </div>
                </div>
              );
            }}
          />
          <Legend
            verticalAlign="top"
            align="left"
            iconType="circle"
            wrapperStyle={{ paddingBottom: 8, fontSize: 12 }}
          />
          <ReferenceLine
            x={targetAge}
            stroke="hsl(var(--muted-foreground))"
            strokeDasharray="3 3"
            label={{
              value: "Retire",
              fill: "hsl(var(--muted-foreground))",
              fontSize: 11,
              position: "insideTopRight",
            }}
          />
          <Area
            type="monotone"
            dataKey="balance"
            name="Projected balance"
            stroke="hsl(var(--primary))"
            fill="url(#gBalance)"
            strokeWidth={2.5}
            activeDot={{ r: 5, strokeWidth: 2, stroke: "hsl(var(--background))" }}
          />
          <Area
            type="monotone"
            dataKey="target"
            name="Target nest egg"
            stroke="hsl(var(--muted-foreground))"
            fill="transparent"
            strokeWidth={1.75}
            strokeDasharray="5 5"
            dot={false}
            activeDot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ProjectionChart;
